import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import DemoFilterSidebar from '../components/DemoFilterSidebar';
import KpiCard from '../components/KpiCard';
import {
  EnergyTimeSeriesChart,
  ConsumptionBySectorChart,
  ConsumptionByDeviceChart,
  TopModulesChart,
  Co2BySectorChart,
} from '../components/charts/demo';
import {
  getFilterOptions,
  getKpis,
  getEnergyByDate,
  getConsumptionBySector,
  getConsumptionByDeviceType,
  getTopModules,
  getCo2BySector,
  getAnomalies,
  type DemoFilters,
  type DemoKpis,
  type EnergyByDate,
  type ConsumptionBySector,
  type ConsumptionByDeviceType,
  type TopModule,
} from '../api/wattelzce-demo';
import { useI18n, formatCO2 } from '../i18n';

// Custom icons for Wattel Demo
const DemoIcons = {
  energy: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  ),
  cost: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  co2: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  modules: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
    </svg>
  ),
};

function WattelzceDemoPage() {
  const { t, language, locale } = useI18n();
  
  // Filter options
  const filterOptions = useMemo(() => getFilterOptions(), []);
  
  // Forecast toggle
  const [forecastEnabled, setForecastEnabled] = useState(false);
  
  // Data state
  const [kpis, setKpis] = useState<DemoKpis | null>(null);
  const [energyByDate, setEnergyByDate] = useState<EnergyByDate[]>([]);
  const [consumptionBySector, setConsumptionBySector] = useState<ConsumptionBySector[]>([]);
  const [consumptionByDevice, setConsumptionByDevice] = useState<ConsumptionByDeviceType[]>([]);
  const [topModules, setTopModules] = useState<TopModule[]>([]);
  const [co2BySector, setCo2BySector] = useState<ConsumptionBySector[]>([]);
  const [anomalies, setAnomalies] = useState<TopModule[]>([]);
  
  // Loading state
  const [loading, setLoading] = useState(true);
  
  // Current filters
  const [currentFilters, setCurrentFilters] = useState<DemoFilters>({});

  // Fetch data based on filters
  const fetchData = useCallback((filters: DemoFilters) => {
    setLoading(true);
    setCurrentFilters(filters);
    
    // Simulate async loading
    setTimeout(() => {
      setKpis(getKpis(filters));
      setEnergyByDate(getEnergyByDate(filters, forecastEnabled));
      setConsumptionBySector(getConsumptionBySector(filters));
      setConsumptionByDevice(getConsumptionByDeviceType(filters));
      setTopModules(getTopModules(filters, 10));
      setCo2BySector(getCo2BySector(filters));
      setAnomalies(getAnomalies(filters));
      setLoading(false);
    }, 300);
  }, [forecastEnabled]);

  // Update when forecast is toggled
  useEffect(() => {
    if (Object.keys(currentFilters).length > 0) {
      setEnergyByDate(getEnergyByDate(currentFilters, forecastEnabled));
    }
  }, [forecastEnabled, currentFilters]);

  // Les libellés « Zone n » / « Inconnu » sont traduits au calcul : on recalcule au changement de langue
  const languageRef = useRef(language);
  useEffect(() => {
    if (languageRef.current === language) return;
    languageRef.current = language;
    if (Object.keys(currentFilters).length > 0) {
      setConsumptionBySector(getConsumptionBySector(currentFilters));
      setConsumptionByDevice(getConsumptionByDeviceType(currentFilters));
      setCo2BySector(getCo2BySector(currentFilters));
    }
  }, [language, currentFilters]);

  const handleFilterChange = useCallback((filters: DemoFilters) => {
    fetchData(filters);
  }, [fetchData]);

  const handleForecastToggle = useCallback((enabled: boolean) => {
    setForecastEnabled(enabled);
  }, []);

  // Format large numbers based on language
  const formatNumber = useCallback((num: number, decimals = 0) => {
    return num.toLocaleString(locale, { maximumFractionDigits: decimals });
  }, [locale]);

  return (
    <div className="flex min-h-screen -m-8">
      {/* Filter Sidebar */}
      <DemoFilterSidebar
        onFilterChange={handleFilterChange}
        filterOptions={filterOptions}
        onForecastToggle={handleForecastToggle}
        forecastEnabled={forecastEnabled}
      />

      {/* Main Content */}
      <main className="flex-1 p-6 bg-gradient-to-br from-slate-50 via-white to-slate-50">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3.5 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-2xl shadow-lg shadow-emerald-200/50">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight">{t('pageTitle')}</h1>
                <p className="text-gray-500 text-sm mt-0.5">{t('pageSubtitle')}</p>
              </div>
            </div>
            
            {/* Forecast indicator */}
            {forecastEnabled && (
              <div className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200/50 rounded-xl shadow-sm">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse shadow-lg shadow-blue-500/50"></div>
                <span className="text-sm font-medium text-blue-700">{t('forecastModeActive')}</span>
              </div>
            )}
          </div>
        </div>

        {/* KPI Cards - Top Bar */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          <KpiCard
            title={t('totalConsumption')}
            value={loading ? '...' : `${formatNumber(kpis?.totalEnergy || 0, 1)} kWh`}
            icon={DemoIcons.energy}
            color="green"
            subtitle={t('cumulativeEnergy')}
          />
          <KpiCard
            title={t('totalCost')}
            value={loading ? '...' : `${formatNumber(kpis?.totalCost || 0, 2)} €`}
            icon={DemoIcons.cost}
            color="blue"
            subtitle={`${t('avgCostPerKwh')}: ${formatNumber(kpis?.avgCostPerKwh || 0, 3)} €`}
          />
          <KpiCard
            title={t('co2Emissions')}
            value={loading ? '...' : formatCO2(kpis?.totalCo2 || 0, language)}
            icon={DemoIcons.co2}
            color="red"
            subtitle={t('environmentalImpact')}
          />
          <KpiCard
            title={t('activeModules')}
            value={loading ? '...' : kpis?.activeModules || 0}
            icon={DemoIcons.modules}
            color="purple"
            subtitle={`${formatNumber((kpis?.totalDuration || 0) / 60, 0)} ${t('hoursOfUse')}`}
          />
        </div>

        {/* Anomaly Alert */}
        {anomalies.length > 0 && !loading && (
          <div className="mb-8 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/70 rounded-2xl p-5 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="p-2.5 bg-amber-100 rounded-xl">
                <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-amber-800">{t('anomaliesDetected')}</h3>
                <p className="text-sm text-amber-700 mt-1">
                  {anomalies.length} {t('modulesWithAnomalies')}:
                  <span className="font-medium ml-1">
                    {anomalies.slice(0, 3).map(a => a.module_name).join(', ')}
                  {anomalies.length > 3 && ` + ${anomalies.length - 3}`}
                  </span>
                </p>
              </div>
              <button className="text-amber-600 hover:text-amber-800 text-sm font-medium hover:underline">
                {t('viewDetails')} →
              </button>
            </div>
          </div>
        )}

        {/* Main Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Energy Time Series - Full Width */}
          <div className="lg:col-span-2">
            <EnergyTimeSeriesChart 
              data={energyByDate} 
              loading={loading}
              showForecast={forecastEnabled}
            />
          </div>
          
          {/* Consumption by Sector */}
          <ConsumptionBySectorChart 
            data={consumptionBySector} 
            loading={loading}
            metric="energy"
          />
          
          {/* Consumption by Device Type */}
          <ConsumptionByDeviceChart 
            data={consumptionByDevice} 
            loading={loading}
          />
          
          {/* Top 10 Modules with Drill-down */}
          <TopModulesChart 
            data={topModules} 
            loading={loading}
            filters={currentFilters}
          />
          
          {/* CO2 by Sector */}
          <Co2BySectorChart 
            data={co2BySector} 
            loading={loading}
          />
        </div>

        {/* Advanced Insights Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Cost Efficiency Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-2.5 bg-blue-50 rounded-xl">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-800">{t('energyEfficiency')}</h3>
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-slate-50 rounded-xl">
                <span className="text-sm text-gray-600">{t('avgCostPerKwh')}</span>
                <span className="font-bold text-blue-600">
                  {formatNumber(kpis?.avgCostPerKwh || 0, 3)} €
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-slate-50 rounded-xl">
                <span className="text-sm text-gray-600">{t('co2RatioKwh')}</span>
                <span className="font-bold text-red-600">
                  {formatNumber((kpis?.totalCo2 || 0) / (kpis?.totalEnergy || 1), 3)} kg
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-slate-50 rounded-xl">
                <span className="text-sm text-gray-600">{t('totalDuration')}</span>
                <span className="font-bold text-purple-600">
                  {formatNumber((kpis?.totalDuration || 0) / 60, 0)}h
                </span>
              </div>
            </div>
          </div>

          {/* Top Consumer Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-2.5 bg-amber-50 rounded-xl">
                <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-800">{t('topConsumer')}</h3>
            </div>
            {topModules[0] && (
              <div className="space-y-3">
                <p className="text-lg font-bold text-gray-900">{topModules[0].module_name}</p>
                <p className="text-sm text-gray-500">{topModules[0].sector} • {topModules[0].device_type}</p>
                <div className="grid grid-cols-3 gap-3 mt-4">
                  <div className="text-center p-3 bg-green-50 rounded-xl">
                    <span className="text-xs text-gray-500 block mb-1">{t('energy')}</span>
                    <span className="font-bold text-green-600 text-sm">{formatNumber(topModules[0].energy, 1)} kWh</span>
                  </div>
                  <div className="text-center p-3 bg-blue-50 rounded-xl">
                    <span className="text-xs text-gray-500 block mb-1">{t('cost')}</span>
                    <span className="font-bold text-blue-600 text-sm">{formatNumber(topModules[0].cost, 2)} €</span>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-xl">
                    <span className="text-xs text-gray-500 block mb-1">CO₂</span>
                    <span className="font-bold text-red-600 text-sm">{formatCO2(topModules[0].co2, language)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Recommendations Card */}
          <div className="bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 rounded-2xl shadow-sm border border-emerald-100 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-2.5 bg-emerald-100 rounded-xl">
                <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="font-semibold text-emerald-800">{t('recommendations')}</h3>
            </div>
            <ul className="space-y-3 text-sm text-emerald-700">
              <li className="flex items-start gap-2 p-2 bg-white/50 rounded-xl">
                <span className="text-emerald-500 mt-0.5">✓</span>
                {t('recommendation1')}
              </li>
              <li className="flex items-start gap-2 p-2 bg-white/50 rounded-xl">
                <span className="text-emerald-500 mt-0.5">✓</span>
                {t('recommendation2')}
              </li>
              <li className="flex items-start gap-2 p-2 bg-white/50 rounded-xl">
                <span className="text-emerald-500 mt-0.5">✓</span>
                {t('recommendation3')}
              </li>
            </ul>
            <button className="mt-4 w-full py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white text-sm font-medium rounded-xl transition-all shadow-lg shadow-emerald-200/50">
              {t('optimizationPlan')} →
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

export default WattelzceDemoPage;
